'use client';

import { Flag } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { type TodoProject, type TodoTask } from '@prisma/client';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Textarea } from '../../../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { createTask } from '../lib/actions/todoActions';
import { DatePicker } from './DatePicker';
import { ProjectPicker } from './ProjectPicker';
import { ReminderPicker } from './ReminderPicker';

interface AddTaskFormProps {
  projects: TodoProject[];
  onTaskAdded: (newTask: TodoTask) => void;
  onProjectCreated: (newProject: TodoProject) => void;
  onCancel: () => void;
}

const priorityMap: Record<number, { label: string; color: string }> = {
  1: { label: 'Срочно', color: 'text-red-500' },
  2: { label: 'Высокий', color: 'text-orange-500' },
  3: { label: 'Средний', color: 'text-sky-500' },
  4: { label: 'Низкий', color: 'text-slate-400' },
};

export function AddTaskForm({
  projects,
  onTaskAdded,
  onProjectCreated,
  onCancel,
}: AddTaskFormProps) {
  const [content, setContent] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [priority, setPriority] = useState(4);
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const [reminderAt, setReminderAt] = useState<Date | undefined>(undefined);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [priorityPopoverOpen, setPriorityPopoverOpen] = useState(false);

  const canSubmit = useMemo(
    () => content.trim().length > 0 && !isSubmitting,
    [content, isSubmitting],
  );

  const resetForm = useCallback(() => {
    setContent('');
    setDescription('');
    setDueDate(undefined);
    setPriority(4);
    setProjectId(undefined);
    setReminderAt(undefined);
    setPriorityPopoverOpen(false);
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (!content.trim() || isSubmitting) return;

      setIsSubmitting(true);
      try {
        const newTask = await createTask({
          content: content.trim(),
          description: description.trim() ? description.trim() : undefined,
          dueDate,
          priority,
          projectId,
          reminderAt,
        });

        toast.success('Задача добавлена!');
        onTaskAdded(newTask);
        resetForm();
      } catch (error: unknown) {
        if (error instanceof Error) toast.error(error.message);
        else toast.error('Ошибка при добавлении задачи.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      content,
      description,
      dueDate,
      priority,
      projectId,
      reminderAt,
      isSubmitting,
      onTaskAdded,
      resetForm,
    ],
  );

  const handleProjectSelect = useCallback((id?: string) => {
    setProjectId(id);
  }, []);

  const handlePriorityPick = useCallback((p: number) => {
    setPriority(p);
    setPriorityPopoverOpen(false);
  }, []);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2 p-2 px-6 md:p-4 md:px-6">
      <div className="flex-grow">
        <Input
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Название задачи, например: 'Купить молоко'"
          className="bg-transparent border-none text-lg text-white placeholder:text-slate-400 focus-visible:ring-0 px-2 mb-4"
          aria-label="Название задачи"
          autoFocus
        />

        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Описание"
          className="bg-transparent border-none text-sm text-slate-300 resize-none focus-visible:ring-0 px-2"
          rows={2}
          aria-label="Описание задачи"
        />
      </div>

      <div className="border-t border-slate-700 pt-2 flex flex-wrap items-center justify-between gap-y-2">
        <div className="flex items-center gap-1">
          <DatePicker date={dueDate} onDateChange={setDueDate} />

          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Popover open={priorityPopoverOpen} onOpenChange={setPriorityPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600"
                    >
                      <Flag size={16} className={`mr-2 ${priorityMap[priority].color}`} />
                      <span className="hidden sm:inline">Приоритет</span>
                    </Button>
                  </PopoverTrigger>

                  <PopoverContent
                    className="w-auto p-1 bg-slate-800 border-slate-700 text-white"
                    align="start"
                  >
                    {Object.entries(priorityMap).map(([p, { label, color }]) => (
                      <Button
                        key={p}
                        type="button"
                        variant="ghost"
                        className="w-full justify-start font-normal hover:bg-slate-700"
                        onClick={() => handlePriorityPick(Number(p))}
                      >
                        <Flag size={16} className={`mr-2 ${color}`} /> {label}
                      </Button>
                    ))}
                  </PopoverContent>
                </Popover>
              </div>
            </TooltipTrigger>

            <TooltipContent>
              <p>Установить приоритет</p>
            </TooltipContent>
          </Tooltip>

          <ReminderPicker
            dueDate={dueDate}
            reminderDate={reminderAt}
            onReminderSet={setReminderAt}
          />
        </div>

        <div className="flex items-center gap-1">
          <ProjectPicker
            projects={projects}
            selectedProjectId={projectId}
            onProjectSelect={handleProjectSelect}
            onProjectCreated={onProjectCreated}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 mt-2 border-t border-slate-700">
        <Button type="button" variant="ghost" className="hover:bg-slate-700" onClick={onCancel}>
          Отмена
        </Button>

        <Button
          type="submit"
          disabled={!canSubmit}
          className="bg-red-600 hover:bg-red-500 text-white"
        >
          {isSubmitting ? 'Добавление...' : 'Добавить задачу'}
        </Button>
      </div>
    </form>
  );
}
